import { useState } from "react";

const topPicks = [
  {
    id: "linen-blend",
    name: "Linen Blend Beige Sofa",
    description: "A soft neutral sofa with a timeless shape that works beautifully in relaxed, layered interiors.",
    priceRange: "Mid-range",
    buttonLabel: "Check Sofa Options",
    icon: "ri-sofa-line",
    tag: "Editor's Pick",
    affiliateUrl: "https://amzn.to/4sd5Qyh",
  },
  {
    id: "deep-seat",
    name: "Deep Seat Cream Sofa",
    description: "A cozy oversized option for a lived-in but elevated soft beige look — ideal for relaxed households.",
    priceRange: "Mid-range to Splurge",
    buttonLabel: "View Similar Sofas",
    icon: "ri-sofa-line",
    tag: "Most Loved",
    affiliateUrl: "https://amzn.to/3PURN2K",
  },
  {
    id: "apartment",
    name: "Minimal Beige Apartment Sofa",
    description: "A clean-lined sofa that fits smaller living rooms while keeping the palette warm and airy.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "See More Sofas",
    icon: "ri-sofa-line",
    tag: "Space Saver",
    affiliateUrl: "https://amzn.to/4bSOBgJ",
  },
];

const budgetSofas = [
  {
    id: "budget-1",
    name: "Affordable Beige Fabric Sofa",
    description: "A budget-friendly option that keeps the same soft neutral tone and relaxed silhouette.",
    priceRange: "Under $600",
    buttonLabel: "View Budget Sofas",
    icon: "ri-sofa-line",
    tag: "Best Value",
    affiliateUrl: "",
  },
  {
    id: "budget-2",
    name: "Simple Oat Sofa",
    description: "Clean lines and a warm oat tone — easy to style and comfortable for everyday use.",
    priceRange: "Under $500",
    buttonLabel: "See Affordable Options",
    icon: "ri-sofa-line",
    tag: "Smart Pick",
    affiliateUrl: "",
  },
];

const midRangeSofas = [
  {
    id: "mid-1",
    name: "Quality Beige Linen Sofa",
    description: "A well-crafted sofa that anchors the room with quiet confidence and lasting comfort.",
    priceRange: "$900–$1,600",
    buttonLabel: "View Sofa Options",
    icon: "ri-sofa-line",
    tag: "Anchor Piece",
    affiliateUrl: "",
  },
  {
    id: "mid-2",
    name: "Rounded Arm Beige Sofa",
    description: "Soft rounded arms and a generous seat depth — the kind of sofa you never want to leave.",
    priceRange: "$800–$1,400",
    buttonLabel: "Explore Mid-Range",
    icon: "ri-sofa-line",
    tag: "Most Comfortable",
    affiliateUrl: "",
  },
];

const splurgeSofas = [
  {
    id: "splurge-1",
    name: "Curved Luxury Beige Sofa",
    description: "A sculptural sofa with premium upholstery that becomes the quiet centerpiece of the room.",
    priceRange: "$2,000+",
    buttonLabel: "View Luxury Sofas",
    icon: "ri-sofa-line",
    tag: "Statement Piece",
    affiliateUrl: "",
  },
  {
    id: "splurge-2",
    name: "Boucle Beige Sectional",
    description: "Rich boucle texture and a generous form — understated luxury at its most liveable.",
    priceRange: "$2,500+",
    buttonLabel: "Explore Premium Sofas",
    icon: "ri-sofa-line",
    tag: "Artisan Craft",
    affiliateUrl: "",
  },
];

const pairWith = [
  {
    id: "rug",
    name: "Neutral Area Rug",
    description: "A soft textured rug that grounds the sofa and defines the seating area.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Rug Options",
    icon: "ri-layout-grid-line",
    tag: "Essential Layer",
    affiliateUrl: "https://amzn.to/4dx4Ykf",
  },
  {
    id: "curtains",
    name: "Linen Curtains",
    description: "Light, airy curtains that soften the room and complement the beige sofa beautifully.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "See Curtain Options",
    icon: "ri-layout-column-line",
    tag: "Easy Upgrade",
    affiliateUrl: "https://amzn.to/4tz8yPK",
  },
  {
    id: "table",
    name: "Light Wood Coffee Table",
    description: "A warm wood table that adds structure and natural contrast to the soft sofa tone.",
    priceRange: "Mid-range",
    buttonLabel: "Explore Coffee Tables",
    icon: "ri-table-line",
    tag: "Finishing Touch",
    affiliateUrl: "https://amzn.to/4tANU1D",
  },
  {
    id: "lamp",
    name: "Warm Floor Lamp",
    description: "A floor lamp placed beside the sofa adds ambient warmth and a sense of intimacy.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Lamp Options",
    icon: "ri-lightbulb-line",
    tag: "Mood Setter",
    affiliateUrl: "https://amzn.to/3O1OR3W",
  },
];

function ProductCard({
  name,
  description,
  priceRange,
  buttonLabel,
  icon,
  tag,
  affiliateUrl,
}: {
  name: string;
  description: string;
  priceRange: string;
  buttonLabel: string;
  icon: string;
  tag: string;
  affiliateUrl: string;
}) {
  const [hovered, setHovered] = useState(false);
  const hasLink = affiliateUrl.trim().length > 0;

  return (
    <div
      className="group border border-[#EDE6DA] rounded-xl p-6 bg-[#FDFAF5] hover:border-[#C8BAA8] transition-all duration-300 flex flex-col gap-4"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F1E8]">
          <i className={`${icon} text-[#8B8378] text-base`} />
        </div>
        <span
          className="text-[#B5A898] text-[9px] tracking-[0.35em] uppercase border border-[#EDE6DA] px-2.5 py-1 rounded-full"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {tag}
        </span>
      </div>

      <h3
        className="text-[#2C2825] text-lg leading-snug"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
      >
        {name}
      </h3>

      <p
        className="text-[#8B8378] text-sm leading-relaxed flex-1"
        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
      >
        {description}
      </p>

      <div className="flex items-center gap-2">
        <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
          Price
        </span>
        <span className="w-4 h-px bg-[#EDE6DA]" />
        <span className="text-[#6B5E53] text-xs" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>
          {priceRange}
        </span>
      </div>

      {hasLink ? (
        <a
          href={affiliateUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#C8BAA8] text-[#6B5E53] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] hover:bg-[#2C2825] hover:text-white hover:border-[#2C2825] transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`ri-arrow-right-line text-[10px] transition-transform duration-200 ${hovered ? "translate-x-0.5" : ""}`} />
          </span>
        </a>
      ) : (
        <div
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#EDE6DA] text-[#C8BAA8] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] whitespace-nowrap select-none"
          style={{ fontFamily: "'Inter', sans-serif" }}
          title="Link coming soon"
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-time-line text-[10px]" />
          </span>
        </div>
      )}
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-5 mb-10">
      <div className="flex-1 h-px bg-[#EDE6DA]" />
      <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>
        {label}
      </p>
      <div className="flex-1 h-px bg-[#EDE6DA]" />
    </div>
  );
}

function ProductGrid({ products, cols = 2 }: { products: typeof topPicks; cols?: 2 | 3 }) {
  return (
    <div className={`grid grid-cols-1 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-5`}>
      {products.map((p) => (
        <ProductCard key={p.id} {...p} />
      ))}
    </div>
  );
}

export default function BSSShopSection() {
  return (
    <section className="max-w-2xl mx-auto px-4 md:px-0 py-4 mb-16">

      {/* Top Picks */}
      <div id="top-picks" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
          Sofa Edit
        </p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Our Top Beige Sofa Picks
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          These are the beige sofas we keep coming back to — chosen for their warm tone, natural upholstery, and ability to anchor a calm living room without overpowering it.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={topPicks} cols={3} />
      </div>

      {/* By Budget */}
      <SectionDivider label="By Budget" />

      <div id="budget-sofas" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
          Budget
        </p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Best Beige Sofas Under $600
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A calm, well-styled beige sofa does not have to be expensive. These picks keep the tone warm and the look considered — without stretching the budget.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={budgetSofas} cols={2} />
      </div>

      <div id="mid-range-sofas" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
          Mid-Range
        </p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Elevated Beige Sofas $800–$1,600
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In this range, material quality and construction improve noticeably. These sofas are built to last and styled to feel genuinely refined.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={midRangeSofas} cols={2} />
      </div>

      <div id="splurge-sofas" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
          Luxury
        </p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Splurge Picks — Luxury Beige Sofas
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          For a more elevated result, these sofas offer exceptional upholstery, considered silhouettes, and the kind of quiet presence that makes a room feel complete.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={splurgeSofas} cols={2} />
      </div>

      {/* Pair With */}
      <SectionDivider label="Complete the Look" />

      <div id="pair-with" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
          Style It
        </p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          What to Pair With a Beige Sofa
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A beige sofa works best when layered with warm textures and natural materials. These are the pieces that complete the look.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={pairWith} cols={2} />
      </div>

    </section>
  );
}
